import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  getTeacherRoster,
  getAttendance,
  saveAttendance,
  getAbsenceReasons,
} from '../../services/teacher';

const STATUS_ORDER = ['', 'present', 'absent', 'late', 'early'];

function cycleStatus(current) {
  const i = STATUS_ORDER.indexOf(current);
  const next = i < 0 ? 1 : (i + 1) % STATUS_ORDER.length;
  return STATUS_ORDER[next];
}

function recordToStatus(rec) {
  if (!rec || !rec.status) return '';
  return rec.status;
}

function statusLabel(s) {
  switch (s) {
    case 'present':
      return 'Present';
    case 'absent':
      return 'Absent';
    case 'late':
      return 'Late';
    case 'early':
      return 'Early';
    default:
      return '—';
  }
}

function statusColor(s) {
  switch (s) {
    case 'present':
      return 'success';
    case 'absent':
      return 'error';
    case 'late':
      return 'warning';
    case 'early':
      return 'info';
    default:
      return 'default';
  }
}

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function AttendanceDayPage() {
  const [date, setDate] = useState(todayStr);
  const [roster, setRoster] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [byStudent, setByStudent] = useState({});
  const [local, setLocal] = useState({});
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadReasons = useCallback(() => {
    getAbsenceReasons()
      .then((res) => setReasons(res.data?.results || res.data || []))
      .catch(() => setReasons([]));
  }, []);

  useEffect(() => {
    loadReasons();
  }, [loadReasons]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getTeacherRoster(), getAttendance(date)])
      .then(([rosterRes, attRes]) => {
        if (cancelled) return;
        const students = rosterRes.data?.results || rosterRes.data || [];
        setRoster(students);
        const records = attRes.data?.results || attRes.data || [];
        const map = {};
        records.forEach((r) => {
          if (r && r.student != null) {
            map[r.student] = r;
          }
        });
        setByStudent(map);
        setLocal({});
        setDirty({});
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  const merged = useMemo(() => {
    const out = {};
    roster.forEach((s) => {
      const id = s.id;
      const base = byStudent[id];
      const st = local[id] !== undefined ? local[id] : recordToStatus(base);
      const reasonKey = `reason_${id}`;
      const defaultReason =
        (base && base.absence_reason) ||
        reasons.find((r) => r.reason_code === 'UNKNOWN')?.reason_code ||
        (reasons[0] && reasons[0].reason_code) ||
        '';
      const reason =
        local[reasonKey] !== undefined ? local[reasonKey] : base?.absence_reason ?? defaultReason;
      out[id] = { student: s, status: st, reason };
    });
    return out;
  }, [roster, byStudent, local, reasons]);

  const hasDirty = Object.keys(dirty).length > 0;

  const shiftDate = (delta) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const setStudentStatus = (studentId, status) => {
    setLocal((prev) => ({ ...prev, [studentId]: status }));
    setDirty((prev) => ({ ...prev, [studentId]: true }));
  };

  const setStudentReason = (studentId, reasonCode) => {
    setLocal((prev) => ({ ...prev, [`reason_${studentId}`]: reasonCode }));
    setDirty((prev) => ({ ...prev, [studentId]: true }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    const records = [];
    Object.keys(dirty).forEach((sid) => {
      const id = Number(sid);
      const row = merged[id];
      if (!row) return;
      let st = row.status;
      let reason = row.reason;
      if (st === 'absent' && !reason) {
        reason = reasons.find((r) => r.reason_code === 'UNKNOWN')?.reason_code || reasons[0]?.reason_code;
      }
      records.push({
        student_id: id,
        status: st,
        absence_reason: st === 'absent' || st === 'late' || st === 'early' ? reason : null,
      });
    });
    try {
      await saveAttendance({ date, records });
      setSuccess('Attendance saved.');
      setDirty({});
      const attRes = await getAttendance(date);
      const list = attRes.data?.results || attRes.data || [];
      const map = {};
      list.forEach((r) => {
        if (r && r.student != null) map[r.student] = r;
      });
      setByStudent(map);
      setLocal({});
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!roster.length) {
    return (
      <Alert severity="info">No students assigned to your classroom.</Alert>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <IconButton onClick={() => shiftDate(-1)} aria-label="Previous day" size="large" sx={{ minWidth: 48, minHeight: 48 }}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ textAlign: 'center', flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ marginTop: 8, fontSize: 16, padding: 8, maxWidth: '100%' }}
          />
        </Box>
        <IconButton onClick={() => shiftDate(1)} aria-label="Next day" size="large" sx={{ minWidth: 48, minHeight: 48 }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {roster.map((s) => {
          const row = merged[s.id];
          const st = row?.status ?? '';
          const showReason = st === 'absent' || st === 'late' || st === 'early';
          const isDirty = !!dirty[s.id];
          return (
            <PaperLike key={s.id} dirty={isDirty}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 0, flex: 1 }}>
                  {s.first_name} {s.last_name}
                </Typography>
                <Chip
                  label={statusLabel(st)}
                  color={statusColor(st)}
                  onClick={() => setStudentStatus(s.id, cycleStatus(st))}
                  sx={{ minHeight: 44, minWidth: 120, fontWeight: 600 }}
                />
              </Box>
              {showReason && (
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel id={`reason-${s.id}`}>Reason</InputLabel>
                  <Select
                    labelId={`reason-${s.id}`}
                    label="Reason"
                    value={row.reason || ''}
                    onChange={(e) => setStudentReason(s.id, e.target.value)}
                    required={st === 'absent'}
                  >
                    {reasons.map((r) => (
                      <MenuItem key={r.reason_code} value={r.reason_code}>
                        {r.reason_label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </PaperLike>
          );
        })}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: { xs: 56, md: 0 },
          p: 2,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 9,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!hasDirty || saving}
          onClick={handleSave}
          sx={{ minHeight: 48 }}
        >
          {saving ? 'Saving…' : 'Save attendance'}
        </Button>
      </Box>
    </Box>
  );
}

function PaperLike({ children, dirty }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: dirty ? 'primary.main' : 'divider',
        borderLeftWidth: dirty ? 4 : 1,
        boxShadow: 1,
      }}
    >
      {children}
    </Box>
  );
}
